import { Button } from "@/atoms-react/button/Button.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { formatCurrencyInput, formatNumber } from "@/features/utils/functions.ts";
import { Themes } from "@/types/theme";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WalletNumber } from "../wallet-number/wallet-number.react";

type ModalBankToCryptoProps = {
  wallet: string | null;
  onSetWallet?: () => void;
  sendKypayToUserWalletPending?: boolean;
  balanceKypay?: number;
  onValidated?: (amount: number) => void;
} & ModalProps;

export const ModalBankToCrypto = ({
  wallet,
  onValidated = () => {},
  onSetWallet = () => {},
  sendKypayToUserWalletPending,
  balanceKypay,
  ...props
}: ModalBankToCryptoProps) => {
  const [amountString, setAmountString] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const resetInputs = () => {
    setAmount(0);
    setAmountString(formatNumber(0));
  };
  const { t } = useTranslation(["wallet"]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    forceCursorPosition();
  }, [amountString]);
  useEffect(() => {
    if (!props.open) {
      resetInputs();
    }
  }, [props]);

  const forceCursorPosition = () => {
    if (inputRef.current) {
      const inputLength = inputRef.current.value.length;
      inputRef.current.setSelectionRange(inputLength, inputLength); // Force cursor to the end on focus
    }
  };

  return (
    <Modal
      className="w-full max-w-lg sm:w-full"
      {...props}
      onClose={() => {
        resetInputs();
        props.onClose?.();
      }}
    >
      <ModalContent title={t("kypay.modal_deposit_funds.label.title")}>
        <div className={"mt-4 opacity-1"}>
          <div className={"mt-4 mb-6 w-full"}>
            <InputLabel
              value={amountString}
              onClick={forceCursorPosition}
              onChange={(value: string) => {
                if (value.length > 16) return;
                const formattedValue = formatCurrencyInput(value);
                const parsed = parseFloat(formattedValue);

                if (!isNaN(parsed)) {
                  setAmount(parsed);
                  setAmountString(formatNumber(parsed));
                } else {
                  setAmountString(formatNumber(0));
                }
              }}
              suffixIcon={<span className="text-black dark:text-white">€</span>}
              type="text"
              min="0.00"
              max="10000.00"
              step="0.01"
              label={t("kypay.modal_transfer_fund.input.amount_label")}
              placeholder={t("kypay.modal_transfer_fund.input.amount_placeholder")}
              ref={inputRef}
            />
          </div>
          <div className={"mt-4 mb-8 w-full"}>
            <WalletNumber
              wallet={wallet}
              label={t("kypay.modal_transfer_fund.label.destination_wallet")}
              onSetWallet={onSetWallet}
            />
          </div>

          <Button
            disabled={
              wallet === null ||
              !amount ||
              amount <= 0 ||
              (balanceKypay !== undefined && balanceKypay < amount)
            }
            shortcut="enter"
            className="shrink-0"
            theme={Themes.primary}
            loading={sendKypayToUserWalletPending}
            onClick={() => {
              if (amount) onValidated(amount);
            }}
            icon={<ChevronRightIcon />}
          >
            {t("kypay.modal_transfer_fund.button.send")}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};
